import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import { uuidv7, type Channel, type ChannelMessage, type CommunityStructure, type MyCommunity, type UserSummary } from "@atrium/shared";
import { api, errorMessage, qs } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useIsWide } from "../../lib/hooks";
import { useUI } from "../../lib/uiStore";
import { getSocket } from "../../lib/socket";
import type { Infinite } from "../../lib/queryClient";
import { useMe } from "../auth/AuthProvider";
import { IconButton } from "../../components/ui/Button";
import { Tooltip } from "../../components/ui/Tooltip";
import { toast } from "../../components/ui/Toast";
import { Spinner } from "../../components/ui/Spinner";
import { RichText } from "../../components/RichText";
import { Avatar } from "../../components/ui/Avatar";
import { IconBack, IconExport, IconPin, IconSidebar, IconAnnouncement, IconShieldWarning, IconTimer, IconX } from "../../components/icons";
import { Button } from "../../components/ui/Button";
import { usePrefs } from "../../lib/prefs";
import { MessageList } from "../chat/MessageList";
import { ChatComposer, type ComposerSend } from "../chat/ChatComposer";
import { gifEmbed } from "../chat/ChatGif";
import { optimisticPoll, pollActions } from "../chat/polls";
import { chatQueryOptions, patchMessage, removeMessage, upsertMessage, useChatFocus, useTyping, type MessagePage } from "../chat/cache";
import { deleteMessageWithUndo } from "../chat/deleteWithUndo";
import type { ChatMessage, MessageAction, MessageActions } from "../chat/types";
import { PrivateMark, channelIcon } from "./ChannelSidebar";
import { permsOf, useMembers } from "./hooks";
import { roleColour } from "./MemberList";
import { chatTimestamp } from "../../lib/format";

type Page = MessagePage<ChannelMessage & { pending?: boolean; failed?: boolean }>;

function PinsPopover({ channelId }: { channelId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isPending } = useQuery({
    queryKey: ["pins", channelId],
    queryFn: () => api.get<{ items: ChannelMessage[] }>(`/api/channels/${channelId}/pins`),
    enabled: open,
  });
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip label="Pinned messages">
        <Popover.Trigger asChild>
          <IconButton label="Pinned messages" icon={IconPin} tooltip={false} active={open} />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={8} className="z-[60] w-[380px] max-w-[calc(100vw-16px)] rounded-lg bg-surface shadow-pop animate-pop-in">
          <div className="border-b border-line px-4 py-3 text-sm font-semibold">Pinned messages</div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {isPending ? (
              <div className="flex justify-center py-8 text-fg-3">
                <Spinner className="size-4" />
              </div>
            ) : !data?.items.length ? (
              <p className="px-2 py-8 text-center text-sm text-fg-3">Nothing has been pinned yet.</p>
            ) : (
              data.items.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    document.getElementById(`m-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                    setOpen(false);
                  }}
                  className="flex w-full gap-2.5 rounded-md p-2 text-left hover:bg-surface-2"
                >
                  <Avatar src={m.author.avatarUrl} name={m.author.displayName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 text-sm">
                      <span className="font-semibold">{m.author.displayName}</span>
                      <span className="text-xs text-fg-3">{chatTimestamp(m.createdAt)}</span>
                    </div>
                    <RichText text={m.content || "Image"} className="line-clamp-3 text-sm text-fg-2" variant="chat" />
                  </div>
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const AGE_KEY = "atrium:age-ok";

/** Age-restricted channels ask once per channel before showing content. */
function AgeGate({ channel, onAccept, onBack }: { channel: Channel; onAccept: () => void; onBack: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-surface-2">
        <IconShieldWarning className="icon size-7 text-warning" aria-hidden />
      </div>
      <h2 className="text-lg font-semibold">#{channel.name} is age-restricted</h2>
      <p className="mt-1 max-w-sm text-sm text-fg-3">It may contain content that's only suitable for adults. Confirm you're 18 or older to continue.</p>
      <div className="mt-5 flex gap-2">
        <Button variant="ghost" onClick={onBack}>
          Go back
        </Button>
        <Button variant="primary" onClick={onAccept}>
          I'm 18 or older
        </Button>
      </div>
    </div>
  );
}

function useAgeOk(channelId: string): [boolean, () => void] {
  const read = () => {
    try {
      return (JSON.parse(localStorage.getItem(AGE_KEY) ?? "[]") as string[]).includes(channelId);
    } catch {
      return false;
    }
  };
  const [ok, setOk] = useState(read);
  useEffect(() => setOk(read()), [channelId]); // eslint-disable-line react-hooks/exhaustive-deps
  const accept = () => {
    try {
      const list = JSON.parse(localStorage.getItem(AGE_KEY) ?? "[]") as string[];
      localStorage.setItem(AGE_KEY, JSON.stringify([...new Set([...list, channelId])].slice(-200)));
    } catch {
      /* storage unavailable: accept for this view only */
    }
    setOk(true);
  };
  return [ok, accept];
}

export function ChannelView({
  structure,
  channel,
  mobile,
  embedded,
  onClose,
}: {
  structure: CommunityStructure;
  channel: Channel;
  mobile?: boolean;
  /** Rendered as a side panel (voice channel chat): compact header, no member toggle. */
  embedded?: boolean;
  onClose?: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const wide = useIsWide();
  const { membersOpen, toggleMembers, openComposer } = useUI();
  const setFocus = useChatFocus((s) => s.setChannel);
  const typing = useTyping((s) => s.typing[`c:${channel.id}`]);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const { community } = structure;
  const prefs = usePrefs();
  // Channel-level permissions (role overrides applied server-side).
  const perms = permsOf(channel.permissions, community.isOwner);
  const communityPerms = permsOf(community.myPermissions, community.isOwner);
  const timedOut = !!community.timeoutUntil && new Date(community.timeoutUntil) > new Date();
  const key = useMemo(() => ["messages", channel.id], [channel.id]);
  const [ageOk, acceptAge] = useAgeOk(channel.id);
  const gated = channel.ageRestricted && !ageOk;
  const Icon = channelIcon(channel);

  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api.get<Page>(`/api/channels/${channel.id}/messages${qs({ before: pageParam as string | undefined, limit: 50 })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMoreBefore ? last.items.find((m) => !m.id.startsWith("pending-"))?.id : undefined),
    ...chatQueryOptions,
  });
  const messages = useMemo(() => (query.data ? [...query.data.pages].reverse().flatMap((p) => p.items) : []), [query.data]);
  const lastId = messages.findLast((m) => !m.id.startsWith("pending-"))?.id;

  useEffect(() => {
    setFocus(channel.id);
    setReplyTo(null);
    return () => setFocus(null);
  }, [channel.id, setFocus]);

  // Mark as read while the channel is on screen.
  const readTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!lastId) return;
    clearTimeout(readTimer.current);
    readTimer.current = setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      void api.post(`/api/channels/${channel.id}/read`, { messageId: lastId }).catch(() => {});
      qc.setQueryData<CommunityStructure>(["structure", community.id], (old) => {
        if (!old) return old;
        const channels = old.channels.map((c) => (c.id === channel.id ? { ...c, unread: false, mentionCount: 0 } : c));
        const stillUnread = channels.some((c) => c.unread);
        const mentions = channels.reduce((n, c) => n + c.mentionCount, 0);
        qc.setQueryData<MyCommunity[]>(["my-communities"], (l) => l?.map((x) => (x.id === community.id ? { ...x, unread: stillUnread, mentionCount: mentions } : x)));
        return { ...old, channels };
      });
    }, 400);
    return () => clearTimeout(readTimer.current);
  }, [lastId, channel.id, community.id, qc]);

  // What each unsent message was sent with (GIF, poll), so Retry resends the same thing.
  const sentInputs = useRef(new Map<string, ComposerSend>());
  const send = useCallback(
    async (input: ComposerSend, retryNonce?: string) => {
      const { content, mediaIds, gif, poll } = input;
      const nonce = retryNonce ?? uuidv7();
      sentInputs.current.set(nonce, input);
      const reply = replyTo;
      setReplyTo(null);
      const optimistic: ChannelMessage & { pending: boolean; failed: boolean } = {
        id: `pending-${nonce}`,
        channelId: channel.id,
        communityId: community.id,
        author: { id: me.id, username: me.username, displayName: me.displayName, avatarUrl: me.avatarUrl },
        content,
        createdAt: new Date().toISOString(),
        editedAt: null,
        interaction: null,
        pinnedAt: null,
        replyTo: reply ? { id: reply.id, author: reply.author, content: reply.content, hasMedia: reply.media.length > 0 } : null,
        media: [],
        kind: "default",
        threadId: null,
        embeds: gif ? [gifEmbed(gif)] : [],
        components: [],
        forwardedFrom: null,
        stickers: [],
        webhook: null,
        suppressEmbeds: false,
        reactions: [],
        mentionIds: [],
        mentionsEveryone: false,
        publishedPostId: null,
        poll: poll ? optimisticPoll(poll) : null,
        nonce,
        pending: true,
        failed: false,
      };
      if (!retryNonce) upsertMessage(key, optimistic);
      else patchMessage<typeof optimistic>(key, `pending-${nonce}`, (m) => ({ ...m, failed: false, pending: true }));
      try {
        const msg = await api.post<ChannelMessage>(`/api/channels/${channel.id}/messages`, { content, mediaIds, replyToId: reply?.id, nonce, gif, poll });
        sentInputs.current.delete(nonce);
        upsertMessage(key, msg);
      } catch (e) {
        patchMessage<typeof optimistic>(key, `pending-${nonce}`, (m) => ({ ...m, failed: true, pending: false }));
        toast.error(errorMessage(e, "Message not sent"));
      }
    },
    [channel.id, community.id, key, me, replyTo],
  );

  const { data: memberPages } = useMembers(community.id);
  const timeoutMap = useMemo(() => new Map((memberPages?.pages.flatMap((p) => p.items) ?? []).map((m) => [m.user.id, m.timeoutUntil])), [memberPages]);
  const colourMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of memberPages?.pages.flatMap((p) => p.items) ?? []) map.set(m.user.id, roleColour(m.roleIds, structure.roles));
    return map;
  }, [memberPages, structure.roles]);

  const memberUsers = useMemo(() => memberPages?.pages.flatMap((p) => p.items.map((m) => m.user)) ?? [], [memberPages]);
  const botIds = useMemo(() => new Set(memberUsers.filter((u) => u.bot).map((u) => u.id)), [memberUsers]);
  const commandContext = useMemo(
    () => ({
      communityId: community.id,
      channelId: channel.id,
      members: memberUsers,
      channels: structure.channels.filter((c) => c.type !== "voice").map((c) => ({ id: c.id, name: c.name })),
    }),
    [community.id, channel.id, memberUsers, structure.channels],
  );

  const actions: MessageActions = {
    authorColour: (id) => colourMap.get(id) ?? null,
    canReply: perms.send && !timedOut,
    canReact: perms.react && !timedOut,
    canPin: perms.manageMessages || perms.pin,
    canModerate: perms.manageMessages,
    onReply: setReplyTo,
    onReact: (m, emoji, add) => {
      patchMessage<ChannelMessage>(key, m.id, (x) => {
        const existing = x.reactions.find((r) => r.emoji === emoji);
        const reactions = existing
          ? x.reactions.map((r) => (r.emoji === emoji ? { ...r, me: add, count: r.count + (add ? 1 : -1) } : r)).filter((r) => r.count > 0)
          : add
            ? [...x.reactions, { emoji, count: 1, me: true }]
            : x.reactions;
        return { ...x, reactions };
      });
      api.put(`/api/messages/${m.id}/reactions`, { emoji, value: add }).catch((e) => toast.error(errorMessage(e)));
    },
    canAttach: perms.attach,
    onEdit: async (m, content, mediaIds) => {
      try {
        const updated = await api.patch<ChannelMessage>(`/api/messages/${m.id}`, { content, ...(mediaIds?.length ? { mediaIds } : {}) });
        patchMessage<ChannelMessage>(key, m.id, () => updated);
      } catch (e) {
        toast.error(errorMessage(e, "Couldn't edit"));
        throw e;
      }
    },
    onDelete: async (m) => {
      if (m.failed) return removeMessage(key, m.id);
      deleteMessageWithUndo(key, m.id, () => api.del(`/api/messages/${m.id}`));
    },
    onPin: async (m) => {
      try {
        const updated = await api.put<ChannelMessage>(`/api/messages/${m.id}/pin`, { value: !m.pinnedAt });
        patchMessage<ChannelMessage>(key, m.id, () => updated);
        void qc.invalidateQueries({ queryKey: ["pins", channel.id] });
        toast.show(updated.pinnedAt ? "Pinned" : "Unpinned");
      } catch (e) {
        toast.error(errorMessage(e));
      }
    },
    onRetry: (m) => void send((m.nonce && sentInputs.current.get(m.nonce)) || { content: m.content, mediaIds: [] }, m.nonce),
    messageLink: (m) => `${window.location.origin}/c/${community.id}/${channel.id}?m=${m.id}`,
    // A 👋 reply to someone's join message; replying pings them. The message arrives over the socket.
    onWave: async (m) => {
      try {
        await api.post(`/api/channels/${channel.id}/messages`, { content: "👋", replyToId: m.id, nonce: uuidv7() });
      } catch (e) {
        toast.error(errorMessage(e));
      }
    },
    poll: pollActions(key),
    moderation: {
      communityId: community.id,
      kick: communityPerms.kick,
      ban: communityPerms.ban,
      timeout: communityPerms.timeout,
      ownerId: community.ownerId,
      timedOutUntil: (id) => timeoutMap.get(id) ?? null,
    },
    extraActions: (m) => {
      const isPublic = community.visibility === "public" && !channel.isPrivate;
      if (!isPublic || m.pending) return [];
      const out: MessageAction[] = [
        {
          key: "share",
          label: "Share to feed",
          icon: IconExport,
          onSelect: () => openComposer({ share: { messageId: m.id, content: m.content, author: m.author, channelName: channel.name, communityName: community.name } }),
        },
      ];
      if (channel.type === "announcement" && perms.publish && community.publishToFeed && !m.publishedPostId)
        out.push({
          key: "publish",
          label: "Publish to feed",
          icon: IconAnnouncement,
          onSelect: async () => {
            try {
              await api.post(`/api/messages/${m.id}/publish`);
              patchMessage<ChannelMessage>(key, m.id, (x) => ({ ...x, publishedPostId: "published" }));
              void qc.invalidateQueries({ queryKey: ["feed"] });
              toast.show(`Published as ${community.name}`);
            } catch (e) {
              toast.error(errorMessage(e));
            }
          },
        });
      return out;
    },
  };

  // `\@Role` and `\#channel` in the composer become raw <@&id> / <#id>.
  const rawRefs = useMemo(() => ({ roles: structure.roles.filter((r) => !r.isDefault), channels: structure.channels }), [structure.roles, structure.channels]);

  const mentionSource = useCallback(
    async (q: string) => {
      try {
        const r = await api.get<{ items: UserSummary[] }>(`/api/channels/${channel.id}/mention-candidates${qs({ q })}`);
        // Candidates don't say which accounts are bots; the member list does.
        return r.items.map((u) => (u.bot || !botIds.has(u.id) ? u : { ...u, bot: true }));
      } catch {
        return [];
      }
    },
    [channel.id, botIds],
  );

  const disabledReason = timedOut
    ? `You're timed out until ${new Date(community.timeoutUntil!).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}.`
    : !perms.send
      ? "You don't have permission to send messages here."
      : channel.type === "announcement" && !perms.postAnnouncements
        ? "Only moderators can post in announcement channels."
        : null;

  const typingNames = prefs.showTyping ? Object.values(typing ?? {}).map((t) => t.name) : [];

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-bg">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        {mobile && !embedded && <IconButton label="Back" icon={IconBack} tooltip={false} onClick={() => navigate(`/c/${community.id}`)} />}
        <Icon className="icon size-5 shrink-0 text-fg-3" aria-hidden />
        <h1 className="truncate text-base font-semibold text-fg">{channel.name}</h1>
        {channel.isPrivate && <PrivateMark className="text-fg-3" />}
        {channel.topic && !embedded && (
          <>
            <span className="hidden h-5 w-px bg-line sm:block" />
            <Tooltip label={channel.topic}>
              <p className="hidden min-w-0 truncate text-sm text-fg-3 sm:block">{channel.topic}</p>
            </Tooltip>
          </>
        )}
        <div className="ml-auto flex items-center">
          {channel.slowModeSeconds > 0 && !embedded && (
            <Tooltip label={`Slow mode: ${channel.slowModeSeconds}s between messages`}>
              <span className="mr-1 flex items-center gap-1 text-xs text-fg-3 tabular">
                <IconTimer className="icon size-4" aria-hidden />
                {channel.slowModeSeconds}s
              </span>
            </Tooltip>
          )}
          <PinsPopover channelId={channel.id} />
          {wide && !embedded && <IconButton label={membersOpen ? "Hide members" : "Show members"} icon={IconSidebar} active={membersOpen} onClick={toggleMembers} />}
          {embedded && onClose && <IconButton label="Close chat" icon={IconX} onClick={onClose} />}
        </div>
      </header>

      {gated ? (
        <AgeGate channel={channel} onAccept={acceptAge} onBack={() => (embedded && onClose ? onClose() : navigate(`/c/${community.id}`))} />
      ) : (
      <MessageList
        messages={messages}
        loading={query.isPending}
        hasMore={!!query.hasNextPage}
        loadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        meId={me.id}
        meUsername={me.username}
        actions={actions}
        intro={
          channel.type === "voice" || channel.type === "stage" ? (
            // Chat beside a room: a quiet note rather than a second hero next to the room's own.
            <div className="px-4 pb-2 pt-6">
              <p className="text-sm text-fg-3">{channel.topic || `Text chat for ${channel.name}. Messages stay after everyone leaves.`}</p>
            </div>
          ) : (
            <div className="px-4 pb-2 pt-10">
              <div className="mb-3 flex size-14 items-center justify-center rounded-2xl bg-surface-2">
                <Icon className="icon size-7 text-fg-2" aria-hidden />
              </div>
              <h2 className="text-2xl font-semibold text-fg">Welcome to #{channel.name}</h2>
              <p className="mt-1 text-sm text-fg-3">
                {channel.topic ||
                  (channel.type === "announcement"
                    ? `This is the start of the #${channel.name} announcement channel. Important updates are posted here.`
                    : `This is the start of the ${channel.isPrivate ? "private " : ""}#${channel.name} channel.`)}
              </p>
            </div>
          )
        }
      />
      )}
      {query.isError && (
        <div className="px-4 pb-2 text-sm text-danger">
          {errorMessage(query.error, "Couldn't load messages")}{" "}
          <button className="underline" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}
      {!gated && (
      <ChatComposer
        draftKey={`c:${channel.id}`}
        placeholder={`Message #${channel.name}`}
        disabledReason={disabledReason}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={(i) => void send(i)}
        allowAttachments={perms.attach}
        allowPolls
        allowGifs
        mentionSource={mentionSource}
        onTyping={() => getSocket()?.emit("channel:typing", { channelId: channel.id })}
        typingNames={typingNames}
        slowModeSeconds={perms.bypassSlowMode ? 0 : channel.slowModeSeconds}
        commands={commandContext}
        rawRefs={rawRefs}
      />
      )}
    </div>
  );
}

